/**
 * PostHog client init (issue #136, PLAN §12 event pipeline). The project key
 * is a PUBLIC/client-embedded identifier (same trust class as the Cognito
 * ids in lib/auth/config.ts) but, unlike those, has no committed literal
 * fallback: an unset key means analytics is a no-op rather than pointing at
 * a shared dev project by accident. Deploy injects the real value as
 * `NEXT_PUBLIC_POSTHOG_KEY` at build time via SSM (see
 * .github/workflows/deploy.yml and infrastructure `ssm.tf`).
 *
 * Posture is deliberately narrower than PostHog's defaults: no autocapture,
 * no session recording, no automatic pageviews — only the named PLAN §12
 * event taxonomy (events.ts) is ever sent.
 */
import posthog from 'posthog-js';

let initialized = false;

export function initAnalytics(): void {
  if (initialized) return;
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return;
  posthog.init(key, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    autocapture: false,
    disable_session_recording: true,
    capture_pageview: false,
  });
  initialized = true;
}

export function isAnalyticsInitialized(): boolean {
  return initialized;
}

/**
 * Ties analytics to the stable Cognito uid (a random IdentityId, never an
 * email) so guest and post-signup events aggregate under one person —
 * mirrors the uid-unchanged invariant lib/auth/AuthProvider.tsx already
 * relies on.
 */
export function identifyUser(uid: string): void {
  if (!initialized) return;
  posthog.identify(uid);
}

/** Test-only: resets module state between test files. */
export function resetAnalyticsForTests(): void {
  initialized = false;
}
